// src/hooks/useProduct.js
import api from '../utils/api';
import useFlashMessage from './useFlashMessage';

export default function useProduct() {
    const { setFlashMessage } = useFlashMessage();

    async function createProduct(token, productData) {
        try {
            const response = await api.post('/products', productData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            setFlashMessage('Produto criado com sucesso!', 'success');
            return { product: response.data.product };
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao criar produto!';
            setFlashMessage(msg, 'error');
            return { error: true, message: msg };
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // getMyProducts sempre retorna um ARRAY de produtos (para o AddInvoice e
    // para qualquer outro lugar que precise da lista diretamente).
    // Além disso expõe limit e plan como propriedades extras do array para o
    // ManageProducts ler sem precisar de uma chamada diferente.
    // ─────────────────────────────────────────────────────────────────────────
    async function getMyProducts(token) {
        try {
            const response = await api.get('/products', {
                headers: { Authorization: `Bearer ${token}` },
            });

            const products = response.data.products || [];
            const limit    = response.data.limit    ?? 5;
            const plan     = response.data.plan     ?? 'free';

            // Anexa limit e plan como propriedades extras no array
            // (arrays são objetos em JS, então isso funciona sem quebrar .filter/.map)
            products.limit = limit;
            products.plan  = plan;

            return products;
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao buscar produtos!';
            setFlashMessage(msg, 'error');
            const empty = [];
            empty.limit = 5;
            empty.plan  = 'free';
            return empty;
        }
    }

    async function getProductById(token, id) {
        try {
            const response = await api.get(`/products/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao buscar produto!';
            setFlashMessage(msg, 'error');
            return null;
        }
    }

    async function searchProducts(token, query) {
        try {
            const response = await api.get('/products/search', {
                headers: { Authorization: `Bearer ${token}` },
                params: { q: query },
            });
            return response.data.products || [];
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao pesquisar produtos!';
            setFlashMessage(msg, 'error');
            return [];
        }
    }

    async function updateProduct(token, id, productData) {
        try {
            const response = await api.patch(`/products/${id}`, productData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            setFlashMessage('Produto atualizado com sucesso!', 'success');
            return { product: response.data.product };
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao atualizar produto!';
            setFlashMessage(msg, 'error');
            return { error: true, message: msg };
        }
    }

    async function deleteProduct(token, id) {
        try {
            const response = await api.delete(`/products/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setFlashMessage('Produto removido com sucesso!', 'success');
            return response.data;
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao remover produto!';
            setFlashMessage(msg, 'error');
            return { error: true };
        }
    }

    return {
        createProduct,
        getMyProducts,
        getProductById,
        searchProducts,
        updateProduct,
        deleteProduct,
    };
}